import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setSceneSpecifierOverride, setSceneMeta } from '../../../../store/visualizerSlice';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './ShowerType.module.css';

// Shower types. Only entries that map to a real scene get a specifier;
// entries without one still record the choice so downstream steps
// (Shower Floor / Shower Wall) know whether they apply.
//
// hasFloor / hasWall determines the visibility of downstream steps.
export const SHOWER_TYPES = [
    {
        key: 'fiberglass-tub-shower',
        label: 'Fiberglass Tub Shower Combo',
        specifier: 'Holland_Homes/Images/2026_Redo/Bath_Fiberglass_Tub_Shower_Combo',
        hasFloor: false,
        hasWall: false
    },
    {
        key: 'fiberglass-shower',
        label: 'Fiberglass Shower',
        specifier: 'Holland_Homes/Images/2026_Redo/Bath_Fiberglass_Tub_Shower_Combo',
        hasFloor: false,
        hasWall: false
    },
    {
        key: 'steel-tub-tile-surround',
        label: 'Steel Tub with Tile Surround',
        specifier: 'Holland_Homes/Images/2026_Redo/Primary_Bathroom',
        hasFloor: false,
        hasWall: true
    },
    {
        key: 'tile-surround-fiberglass-pan',
        label: 'Tile Surround, Fiberglass Pan',
        specifier: 'Holland_Homes/Images/2026_Redo/Primary_Bathroom',
        hasFloor: false,
        hasWall: true
    },
    {
        key: 'full-tile-shower',
        label: 'Full Tile Shower',
        specifier: 'Holland_Homes/Images/2026_Redo/Primary_Bathroom',
        hasFloor: true,
        hasWall: true
    }
];

function thumbUrl(specifier) {
    return `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(specifier)}&sel=-1&type=base&size=508`;
}

// Given a shower-type key, return { hasFloor, hasWall }.
// Used by VisualizerPage to gate Shower Floor / Shower Wall steps.
export function getShowerCapabilities(showerTypeKey) {
    const t = SHOWER_TYPES.find(s => s.key === showerTypeKey);
    if (!t) return { hasFloor: false, hasWall: false };
    return { hasFloor: !!t.hasFloor, hasWall: !!t.hasWall };
}

export default function ShowerType({
    stepIndex,
    room = 'Bathroom',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const sceneMeta = useSelector(state => state.visualizer.sceneMeta);
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const floorplans = useSelector(state => state.floorplan.floorplans);

    const communityCode = useMemo(() => {
        const fp = (floorplans || []).find(f => String(f.Id || f.id) === String(activeFloorplanId));
        return String(fp?.Code || fp?.code || '').trim().toUpperCase();
    }, [floorplans, activeFloorplanId]);

    // Default suggested selection based on the community — PR/AF start on Primary Bath,
    // others on the fiberglass tub shower combo.
    const suggestedKey = communityCode === 'PR' || communityCode === 'AF'
        ? 'full-tile-shower'
        : 'fiberglass-tub-shower';

    const selectedKey = sceneMeta?.[room]?.showerType || suggestedKey;

    const handleSelect = (opt) => {
        dispatch(setSceneSpecifierOverride({ area: room, specifier: opt.specifier }));
        dispatch(setSceneMeta({
            area: room,
            meta: {
                showerType: opt.key,
                showerHasFloor: !!opt.hasFloor,
                showerHasWall: !!opt.hasWall
            }
        }));
    };

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title="Shower Type"
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.instructions}>
                Choose the shower configuration. Additional tile steps will appear when applicable.
            </div>

            <div className={styles.grid}>
                {SHOWER_TYPES.map(opt => (
                    <button
                        key={opt.key}
                        type="button"
                        className={`${styles.card} ${selectedKey === opt.key ? styles.cardActive : ''}`}
                        onClick={() => handleSelect(opt)}
                    >
                        <img
                            src={thumbUrl(opt.specifier)}
                            alt={opt.label}
                            className={styles.thumb}
                        />
                        <div className={styles.title}>{opt.label}</div>
                        <div className={styles.sub}>
                            {opt.hasFloor || opt.hasWall
                                ? `Tile ${opt.hasFloor ? 'floor' : ''}${opt.hasFloor && opt.hasWall ? ' & ' : ''}${opt.hasWall ? 'walls' : ''}`
                                : 'No tile customization'}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

ShowerType.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
